import { apiFetch, apiFetchBlob } from "@/lib/auth-api";

/**
 * Device-agent control plane (Phase 4). Backend sends camelCase; paths go
 * through apiFetch which prepends `/api` + Bearer — so pass them WITHOUT `/api`.
 */

export type DeviceStatus = "unassigned" | "online" | "offline" | "error" | "revoked";

export type DeviceRole =
	| "pharmacy_workstation"
	| "office_laptop"
	| "it_admin"
	| "server"
	| "natubay"
	| "franchise"
	| "franchise_managed"
	| "franchise_other"
	| string;

/**
 * Per-device connection policy.
 * - "unattended": connect WITHOUT confirmation on the target device.
 * - "confirm": the user at the device MUST accept the incoming connection.
 */
export type ConnectionPolicy = "unattended" | "confirm";

export interface DeviceAgent {
	deviceUuid: string;
	tenantId: number | null;
	idfirm: number | null;
	deviceRole: DeviceRole;
	displayName: string | null;
	hostname: string | null;
	status: DeviceStatus;
	enrollmentState: string | null;
	rustdeskId: string | null;
	lastSeenAt: string | null;
	helperVersion: string | null;
	serviceVersion: string | null;
	/** Metadane builda agenta z `hello` (`g<sha7> <data> <x86|x64>`), tylko diagnoza. */
	buildInfo?: string | null;
	rustdeskVersion: string | null;
	isCanary: boolean;
	asystentEnabled?: boolean;
	/**
	 * Raw per-device override. `null` = no override → resolved automatically by
	 * device role (see `effectiveConnectionPolicy`).
	 */
	connectionPolicy: ConnectionPolicy | null;
	/** Resolved policy actually in force (override, or the role default). */
	effectiveConnectionPolicy: ConnectionPolicy;
	/** Free-form admin note (location, quirks) — editable from the devices panel. */
	operatorNote: string | null;
	createdAt: string;
	updatedAt: string;
	snapshot: unknown;
	collectedAt: string | null;
	/**
	 * Live WebSocket presence from the backend hub — the SAME truth the
	 * remote-session gate uses. Prefer this over `status` for "can I connect
	 * right now": the `status` column lags (grace period, stale right after a
	 * backend restart), so a green badge driven by `status` can promise a
	 * connection that immediately 409s. See `isDeviceReachable`.
	 */
	hubOnline: boolean;
	/**
	 * Czy urządzenie ma aktywne zdarzenie regułowe (uptime/dysk). Na zwiniętym
	 * kaflu pokazuje sam wykrzyknik; szczegóły (`listDeviceEventIncidents`)
	 * ładuje widok rozwinięty.
	 */
	hasActiveEvent: boolean;
	/**
	 * Zajętość dysku C: (%) z ostatniego heartbeatu agenta. `null` gdy agent pola
	 * nie wysyła (starsze wersje); `undefined` gdy backend sprzed tej kolumny
	 * (Vercel potrafi wdrożyć się przed backendem).
	 */
	vitalsDiskCUsedPct?: number | null;
	/**
	 * Czas ostatniego heartbeatu Z POMIAREM dysku — wiek `vitalsDiskCUsedPct`.
	 * Nie ogólny `vitals_at`: ten odświeża też heartbeat bez pomiaru dysku.
	 */
	vitalsDiskAt?: string | null;
	/**
	 * Nazwa apteki (`farm_firm`) dla `idfirm`, doklejana przez backend. Kluczowa
	 * dla aptek udostępnionych przez delegację — te są poza własnym drzewem
	 * `/scope/tree`, więc tylko stąd da się poznać ich nazwę zamiast „Apteka
	 * <idfirm>". `null` gdy urządzenie bez apteki lub nazwy brak.
	 */
	pharmacyName: string | null;
	/** Previous assigned device recognized as the only safe recovery target. */
	recoveryDeviceUuid?: string | null;
}

export type RemoteSessionOutcome =
	| "preparing"
	| "pending"
	| "launched"
	| "connected"
	| "failed"
	| "cancelled"
	| "timeout";

export interface RemoteSession {
	sessionId: string;
	/**
	 * Lifecycle state. `preparing` is the new INITIAL state — the target device
	 * is spinning up an isolated portable RustDesk and handshaking back its
	 * portable id. Once that completes the session moves `preparing → pending`
	 * with `preparedRustdeskId` populated, then `launched`/`connected`.
	 */
	outcome: RemoteSessionOutcome;
	controllerDeviceUuid: string;
	targetDeviceUuid?: string;
	/** Present in the POST response, not always in the GET status payload. */
	targetRustdeskId?: string;
	/**
	 * Portable RustDesk id handed back by the target after takeover. Present once
	 * the session reaches `pending`/`launched`. Sensitive — only ever returned to
	 * the session controller; never log it.
	 */
	preparedRustdeskId?: string;
	failureReason?: string | null;
	startedAt?: string | null;
	endedAt?: string | null;
}

export interface CreateRemoteSessionInput {
	targetDeviceUuid: string;
	controllerDeviceUuid: string;
}

/**
 * On-demand device diagnostics (Phase 5). Mirrors the backend contract:
 * disk/uptime/boot/ram collected live from the agent or served from the last
 * cached snapshot when the device is offline / does not answer in time.
 */
export interface DeviceDiagnostics {
	diskCFreeGb: number | null;
	diskCTotalGb: number | null;
	diskCFreePct: number | null;
	uptimeSec: number | null;
	lastBootAt: string | null;
	/** 0=full boot, 1=fast-startup, 2=resume-from-hibernate; null = unknown/non-Windows. */
	bootType: number | null;
	ramTotalMb: number | null;
	ramAvailableMb: number | null;
	cpuModel: string | null;
	cpuLogicalCount: number | null;
	/** Jobs queued in the Windows print spooler; null on non-Windows / unknown. */
	printerQueueCount: number | null;
	/**
	 * Stan usługi Windows naszego klienta RustDesk: "running" | "starting" |
	 * "stopped" | "absent" | "other" | "unknown" (0.1.113+: `sc query` padło
	 * albo zawisło). `null` = agent starszy niż 0.1.101 albo nie-Windows.
	 */
	rustdeskServiceState?: string | null;
	/** Local IP address of the device; null when unavailable. */
	localIp: string | null;
	/** Ostatnie ~15-min okno obciążenia z heartbeatu; brak = stary agent. */
	metrics?: DeviceMetricsWindow | null;
}

export interface SpoolerRestartResponse {
	ok: boolean;
	error: string | null;
}

export interface OfficeKillResponse {
	ok: boolean;
	/** Ile procesów Office ubito; null gdy agent nie zwrócił liczby. */
	killed: number | null;
	error: string | null;
}

export interface DbOnlineResponse {
	ok: boolean;
	error: string | null;
}

export interface UnlockServiceAccountResponse {
	ok: boolean;
	error: string | null;
	/** Ustawione tylko dla stanowisk: adres serwera, do którego dopisano poświadczenie. */
	serverIp?: string | null;
}

export type DiagnosticsSource = "live" | "cached" | "unavailable";

export interface DeviceDiagnosticsResponse {
	source: DiagnosticsSource;
	deviceOnline: boolean;
	/** When the diagnostics in this payload were actually collected. */
	collectedAt: string | null;
	diagnostics: DeviceDiagnostics | null;
}

/**
 * Podsumowanie metryk obciążenia (CPU/RAM/dysk) z ostatnich 30 dni pracy
 * urządzenia. `typical` = mediana ze średnich 15-minutowych okien ("typowe
 * wykorzystanie"), `peak` = p95 z maksimów okien. Każdy kanał może być null
 * (niemierzony — np. liczniki dysku wyłączone), null ≠ 0.
 */
export interface MetricsTriple {
	cpuPct: number | null;
	ramPct: number | null;
	diskBusyPct: number | null;
}

export interface MetricsDailyPoint {
	date: string;
	cpuPctAvg: number | null;
	ramPctAvg: number | null;
	diskBusyPctAvg: number | null;
}

export interface DeviceMetricsSummary {
	days: number;
	/** Liczba 15-minutowych okien w okresie ≈ godziny pracy maszyny / 4. */
	windowCount: number;
	firstSampleAt: string | null;
	typical: MetricsTriple;
	peak: MetricsTriple;
	daily: MetricsDailyPoint[];
}

/** Ostatnie okno metryk z heartbeatu (klucz `metrics` w snapshot/diagnostyce). */
export interface DeviceMetricsWindow {
	windowStartAt: string;
	windowEndAt: string;
	sampleCount: number;
	cpuPctAvg: number | null;
	cpuPctMax: number | null;
	ramPctAvg: number | null;
	ramPctMax: number | null;
	diskBusyPctAvg: number | null;
	diskBusyPctMax: number | null;
}

export function getDeviceMetricsSummary(deviceUuid: string): Promise<DeviceMetricsSummary> {
	return apiFetch<DeviceMetricsSummary>(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/metrics-summary`,
	);
}

/**
 * Pomiar jakości internetu (agent ≥ 0.1.114, metoda `cf-v1`). Seria obejmuje
 * całą lokalizację urządzenia (wszystkie stanowiska apteki albo samo
 * urządzenie bez `idfirm`); test w toku jest w `pending`, nie w `points`.
 * Każda wartość pomiaru może być null — test nieudany zachowuje to, co zdążył
 * zmierzyć przed błędem, a null ≠ 0.
 */
export type NetworkTestDays = 7 | 30 | 90;
export type NetworkTestStatus = "pending" | "ok" | "failed";

export interface NetworkTestPoint {
	testId: number;
	deviceUuid: string;
	trigger: "schedule" | "manual";
	status: NetworkTestStatus;
	startedAt: string;
	latencyMs: number | null;
	jitterMs: number | null;
	downloadMbps: number | null;
	uploadMbps: number | null;
	bytesDown: number | null;
	bytesUp: number | null;
	durationMs: number | null;
	serverColo: string | null;
	/** Kod przyczyny porażki (dns, connect, tls, http, timeout, io, busy, internal, no_response, …). */
	error: string | null;
	errorPhase: "latency" | "download" | "upload" | null;
	httpStatus: number | null;
}

export interface NetworkTestPending {
	testId: number;
	deviceUuid: string;
	startedAt: string;
}

export interface NetworkTestDeviceLabel {
	deviceUuid: string;
	label: string;
}

export interface DeviceNetworkTests {
	days: number;
	deviceUuid: string;
	/** `device` = urządzenie bez apteki — seria to wyłącznie ono. */
	locationKind: "pharmacy" | "device";
	idfirm: number | null;
	/** Wyłącznik pomiaru floty i interwał rotacji (Konfiguracja → Aktualizacje). */
	enabled: boolean;
	intervalMinutes: number;
	minAgentVersion: string;
	serviceVersion: string | null;
	agentSupported: boolean;
	devices: NetworkTestDeviceLabel[];
	pending: NetworkTestPending | null;
	/** Rosnąco po `startedAt`, bez testu w toku. */
	points: NetworkTestPoint[];
}

export interface StartNetworkTestResponse {
	testId: number;
	status: "pending";
	startedAt: string;
}

export function getDeviceNetworkTests(
	deviceUuid: string,
	days: NetworkTestDays,
): Promise<DeviceNetworkTests> {
	return apiFetch<DeviceNetworkTests>(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/network-tests?days=${days}`,
	);
}

/**
 * „Zmierz teraz” — zleca pojedynczy test (tylko urządzenie online). Wynik
 * przychodzi asynchronicznie; 409 niesie `code`: device_offline,
 * agent_outdated, test_in_progress albo device_unreachable.
 */
export function startDeviceNetworkTest(deviceUuid: string): Promise<StartNetworkTestResponse> {
	return apiFetch<StartNetworkTestResponse>(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/network-tests`,
		{ method: "POST" },
	);
}

/**
 * Samonaprawa dostępu do udziału KS-AOW (`\\SERWER\ks`) — tylko apteki własne.
 * Agent raportuje stan udziału ramką `share_health`; backend trzyma ostatni
 * raport i historię zmian oraz własne naprawy (odblokowanie konta `serwis` na
 * serwerze apteki, odświeżenie poświadczenia na prośbę stanowiska).
 */
export type ShareRole = "station" | "server" | "mapped_drive" | "unknown";
export type ShareFault =
	| "access_denied"
	| "password_expired"
	| "share_missing"
	| "unreachable"
	| "other";
export type ShareDecision =
	| "healthy"
	| "disabled"
	| "wait"
	| "skip_network"
	| "server_side"
	| "act"
	| "exhausted"
	| "report_only";
/** Zdarzenia backendu: odblokowanie konta na serwerze / prośba stanowiska. */
export type ShareEventDecision = ShareDecision | "server_repair" | "repair_request";
export type ShareAction = "reconnect_share" | "refresh_credential" | "unlock_server_account";
export type ShareSelfHealMode = "repair" | "observe" | "off";

/** Antywirus ESET na urządzeniu (`managed` = zarządzany przez ESET PROTECT). */
export interface ShareEset {
	product: string;
	version: string;
	managed: boolean;
}

/** Stan lokalnego konta `serwis` z raportu serwera KS (`role = server`). */
export interface ShareServiceAccount {
	exists: boolean;
	disabled: boolean;
	locked: boolean;
	passwordExpired: boolean;
}

export interface ShareHealthCurrent {
	role: ShareRole;
	server: string | null;
	share: string | null;
	ok: boolean | null;
	fault: ShareFault | null;
	osError: number | null;
	serverReachable: boolean | null;
	faultSinceSecs: number | null;
	decision: ShareDecision;
	action: ShareAction | null;
	actionExecuted: boolean;
	mode: ShareSelfHealMode;
	observedAt: string;
	updatedAt: string;
	eset: ShareEset | null;
	/** `null` = nieodczytane albo nie dotyczy (stanowisko). */
	serviceAccount: ShareServiceAccount | null;
	/** IPv4 serwera KS z jego raportu (`role = server`); `null` = brak albo inna rola. */
	localIp: string | null;
}

/** `detail` zdarzeń backendu (`server_repair`, `repair_request`). */
export interface ShareEventDetail {
	/** `dispatched` = komenda wysłana, wyniku jeszcze nie ma (liczy się do budżetu). */
	outcome?: "executed" | "dispatched" | "observe" | "refused";
	reason?: string;
	message?: string | null;
	result?: { ok: boolean; error: string | null };
	stations?: { deviceUuid: string; fault: ShareFault | null }[];
}

export interface ShareHealthEvent {
	eventId: number;
	observedAt: string;
	receivedAt: string;
	role: ShareRole | null;
	server: string | null;
	share: string | null;
	ok: boolean | null;
	fault: ShareFault | null;
	osError: number | null;
	serverReachable: boolean | null;
	faultSinceSecs: number | null;
	decision: ShareEventDecision;
	action: ShareAction | null;
	actionExecuted: boolean;
	mode: ShareSelfHealMode | null;
	serviceAccount: ShareServiceAccount | null;
	detail: ShareEventDetail | null;
}

/**
 * Stanowisko nie dostaje odpowiedzi od serwera apteki, choć serwer jest online
 * (najczęściej blokuje je ESET albo zapora na serwerze).
 */
export interface ShareBlockedByServer {
	serverHostname: string | null;
	/** ESET z raportu serwera; `null` = brak albo nieznany. */
	serverEset: ShareEset | null;
}

export interface DeviceShareHealth {
	/** `null` = urządzenie nie raportowało stanu udziału (sekcja ukryta). */
	current: ShareHealthCurrent | null;
	/** Ostatnie zdarzenia, od najnowszego (najwyżej 20). */
	events: ShareHealthEvent[];
	/** Brak pola = starszy backend; `null` = nie dotyczy. */
	blockedByServer?: ShareBlockedByServer | null;
	/** Stanowisko: APMAN.INI wskazuje stary adres serwera. */
	staleTarget?: ShareStaleTarget | null;
	/** Serwer: stanowiska dostają odmowę dostępu (kod 5) do udziału. */
	shareAccessDenied?: ShareAccessDenied | null;
}

/** Stanowisko szuka serwera KS pod starym adresem (`KATALOG_SIECIOWY` w APMAN.INI). */
export interface ShareStaleTarget {
	target: string;
	serverIp: string;
	serverHostname: string | null;
}

/** Udział KS na serwerze nie jest udostępniony kontu `serwis`. */
export interface ShareAccessDenied {
	share: string;
	stations: string[];
}

export interface KsShareFixResponse {
	ok: boolean;
	error: string | null;
	result: Record<string, unknown> | null;
}

/** Stanowisko: przepisz stary adres serwera w APMAN.INI (z kopią zapasową). */
export function retargetDeviceApman(deviceUuid: string): Promise<KsShareFixResponse> {
	return apiFetch<KsShareFixResponse>(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/apman-retarget`,
		{ method: "POST" },
	);
}

/** Serwer KS: nadaj kontu `serwis` dostęp do udziału (Zmiana + NTFS Modyfikacja). */
export function grantDeviceKsShareAccess(deviceUuid: string): Promise<KsShareFixResponse> {
	return apiFetch<KsShareFixResponse>(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/grant-ks-share-access`,
		{ method: "POST" },
	);
}

export function getDeviceShareHealth(deviceUuid: string): Promise<DeviceShareHealth> {
	return apiFetch<DeviceShareHealth>(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/share-health`,
	);
}

export function listDevices(): Promise<DeviceAgent[]> {
	return apiFetch<DeviceAgent[]>("/device-agent/devices");
}

/** Heavy single-device row — fetched lazily only when a tile expands. */
export function getDeviceDetail(deviceUuid: string): Promise<DeviceAgent> {
	return apiFetch<DeviceAgent>(`/device-agent/devices/${encodeURIComponent(deviceUuid)}`);
}

/** Live-or-cached diagnostics for one device — fetched lazily on tile expand. */
export function getDeviceDiagnostics(deviceUuid: string): Promise<DeviceDiagnosticsResponse> {
	return apiFetch<DeviceDiagnosticsResponse>(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/diagnostics`,
	);
}

/** Restart the Windows Print Spooler on a reachable device (agent runs as SYSTEM). */
export function restartDeviceSpooler(deviceUuid: string): Promise<SpoolerRestartResponse> {
	return apiFetch<SpoolerRestartResponse>(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/restart-spooler`,
		{ method: "POST" },
	);
}

/** Kill hung LibreOffice/OpenOffice processes on a reachable device (SYSTEM). */
export function killDeviceOffice(deviceUuid: string): Promise<OfficeKillResponse> {
	return apiFetch<OfficeKillResponse>(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/kill-office`,
		{ method: "POST" },
	);
}

/**
 * Przełącz bazę KS-Apteki (WAPTEKA.fdb) w tryb online — agent weryfikuje
 * istnienie pliku bazy i odpala `gfix -online` jako SYSTEM.
 */
export function setDeviceDbOnline(deviceUuid: string): Promise<DbOnlineResponse> {
	return apiFetch<DbOnlineResponse>(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/db-online`,
		{ method: "POST" },
	);
}

/**
 * Serwer apteki: odblokuj lokalne konto serwisowe „serwis" — agent ustawia znane
 * hasło i włącza konto (`net user serwis …` + `/active:yes`) jako SYSTEM, zdejmuje
 * blokadę po błędnych logowaniach i ustawia „hasło nigdy nie wygasa”.
 * Stanowisko: takiego konta tam nie ma, więc agent zamiast tego dopisuje
 * poświadczenie do serwera apteki w sejfie Windows zalogowanego użytkownika.
 * Hasło żyje tylko w agencie.
 */
export function unlockDeviceServiceAccount(
	deviceUuid: string,
): Promise<UnlockServiceAccountResponse> {
	return apiFetch<UnlockServiceAccountResponse>(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/unlock-service-account`,
		{ method: "POST" },
	);
}

export interface RotatePasswordResponse {
	ok: boolean;
	error: string | null;
}

/**
 * Wymuś nowe stałe hasło RustDesk na urządzeniu — odcina czasowy dostęp
 * zewnętrzny (stare hasło przestaje działać). Wymaga online (offline → 409).
 */
export function rotateRustdeskPassword(deviceUuid: string): Promise<RotatePasswordResponse> {
	return apiFetch<RotatePasswordResponse>(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/rotate-rustdesk-password`,
		{ method: "POST" },
	);
}

export interface PurgeRustdeskResponse {
	ok: boolean;
	error: string | null;
}

/**
 * Wyczyść instalację RustDeska na urządzeniu pod świeżą reinstalację: agent
 * zatrzymuje i usuwa usługę Windows, kasuje pliki, konfigurację i hasło, po czym
 * jego provisioning stawia klienta od zera. Wymaga online (offline → 409).
 */
export function purgeDeviceRustdesk(deviceUuid: string): Promise<PurgeRustdeskResponse> {
	return apiFetch<PurgeRustdeskResponse>(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/purge-rustdesk`,
		{ method: "POST" },
	);
}

/** Wynik zlecenia aktualizacji agenta na jednym urządzeniu. */
export interface AgentUpdateResponse {
	/** Jednorazowa kampania (status `completed`), w której ląduje wynik tego urządzenia. */
	campaignId: number;
	commandId: number;
	versionFrom: string;
	versionTo: string;
}

/**
 * Zainstaluj na JEDNYM urządzeniu bieżący instalator z półki — z pominięciem
 * etapu canary kampanii. Wersję i SHA backend czyta z półki sam;
 * `expectedVersion` to wersja, którą operator widział i potwierdził — gdy półka
 * się w międzyczasie zmieniła, backend odpowiada 409 zamiast wysłać inną wersję.
 * Wymaga online (offline → 409).
 */
export function requestAgentUpdate(
	deviceUuid: string,
	expectedVersion: string,
): Promise<AgentUpdateResponse> {
	return apiFetch<AgentUpdateResponse>(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/update-agent`,
		{ method: "POST", body: JSON.stringify({ expectedVersion }) },
	);
}

/** Notatka „na urządzeniu" — okno helpera zawsze-na-wierzchu u odbiorcy. */
export interface DeviceNote {
	noteId: number;
	deviceUuid: string;
	authorUserId: number;
	authorName: string;
	body: string;
	createdAt: string;
	/** null dopóki odbiorca nie zamknie okna notatki. */
	readAt: string | null;
}

/** Zostaw notatkę na urządzeniu (wymaga online — offline → 409). */
export function leaveDeviceNote(deviceUuid: string, body: string): Promise<DeviceNote> {
	return apiFetch<DeviceNote>(`/device-agent/devices/${encodeURIComponent(deviceUuid)}/notes`, {
		method: "POST",
		body: JSON.stringify({ body }),
	});
}

export interface LiteConnectToken {
	/** `farmaster://connect/<token>` — opened to launch FarmasterAgent Lite. */
	deeplink: string;
	expiresInSeconds: number;
}

/**
 * Mint a short-lived `farmaster://connect` token for a NON-enrolled operator
 * computer running FarmasterAgent Lite. The operator JWT authorizes it (same
 * device-visibility check as a normal remote session); the Lite app exchanges
 * the token for the one-time RustDesk credential over the no-`fd_` path.
 */
export function mintLiteConnectToken(deviceUuid: string): Promise<LiteConnectToken> {
	return apiFetch<LiteConnectToken>("/device-agent/remote-sessions/lite-token", {
		method: "POST",
		body: JSON.stringify({ targetDeviceUuid: deviceUuid }),
	});
}

export interface RecentSessionProbe {
	/** True when the FULL agent already initiated a session for this target. */
	initiated: boolean;
}

/**
 * Probe whether the enrolled (full-agent) flow just created a session for this
 * target — the one-button fallback signal. If false after ~3s the SPA opens the
 * Lite deeplink instead.
 */
export function getRecentSessionForTarget(deviceUuid: string): Promise<RecentSessionProbe> {
	return apiFetch<RecentSessionProbe>(
		`/device-agent/remote-sessions/recent-for-target/${encodeURIComponent(deviceUuid)}`,
	);
}

/** One entry of the signed-in operator's „Ostatnie połączenia" history. */
export interface RecentConnection {
	deviceUuid: string;
	lastConnectedAt: string;
}

/** The operator's 5 most-recent connection targets, newest first (per-user). */
export function listRecentConnections(): Promise<RecentConnection[]> {
	return apiFetch<RecentConnection[]>("/device-agent/recent-connections");
}

/**
 * Record that the operator just started a connection to a device — upsert, so a
 * reconnect bumps it to the top rather than duplicating. Fire-and-forget from
 * „Połącz"; a failure here must never block or fail the actual connect.
 */
export function recordRecentConnection(deviceUuid: string): Promise<{ ok: boolean }> {
	return apiFetch<{ ok: boolean }>("/device-agent/recent-connections", {
		method: "POST",
		body: JSON.stringify({ deviceUuid }),
	});
}

/** Historia notatek urządzenia (najnowsze pierwsze). */
export function listDeviceNotes(deviceUuid: string): Promise<DeviceNote[]> {
	return apiFetch<DeviceNote[]>(`/device-agent/devices/${encodeURIComponent(deviceUuid)}/notes`);
}

/**
 * Set (or clear) the per-device connection-policy override. Passing `null`
 * clears the override so the policy falls back to the role default.
 */
export function updateDeviceConnectionPolicy(
	deviceUuid: string,
	policy: ConnectionPolicy | null,
): Promise<DeviceAgent> {
	return apiFetch<DeviceAgent>(`/device-agent/devices/${encodeURIComponent(deviceUuid)}`, {
		method: "PATCH",
		body: JSON.stringify({ connectionPolicy: policy }),
	});
}

/**
 * Marks (or unmarks) the device as a CANARY — the early-rollout cohort. Update
 * campaigns in the "canary" stage only target devices with this flag, so an
 * operator can test a new agent version on one machine before the full fleet.
 */
export function updateDeviceCanary(deviceUuid: string, isCanary: boolean): Promise<DeviceAgent> {
	return apiFetch<DeviceAgent>(`/device-agent/devices/${encodeURIComponent(deviceUuid)}`, {
		method: "PATCH",
		body: JSON.stringify({ isCanary }),
	});
}

export function updateAsystentEnabled(
	deviceUuid: string,
	asystentEnabled: boolean,
): Promise<DeviceAgent> {
	return apiFetch<DeviceAgent>(`/device-agent/devices/${encodeURIComponent(deviceUuid)}`, {
		method: "PATCH",
		body: JSON.stringify({ asystentEnabled }),
	});
}

// ── Opt-in franczyz do kanału powiadomień (edukacja dymkami) ─────────────────

/**
 * Zgoda jest cechą APTEKI (`idfirm`) w organizacji URZĄDZENIA (`tenantId`),
 * nie urządzenia — obejmuje wszystkie jej stanowiska
 * `franchise`/`franchise_managed`. Apteki własne są objęte kanałem zawsze i nie
 * mają wpisów.
 */
export interface NotificationOptinEntry {
	idfirm: number;
	enabledAt: string;
	/** Nazwa konta, które włączyło; null gdy konto usunięto albo włączał
	 *  super_admin z innej organizacji (aktor jest w audycie jego organizacji). */
	enabledBy: string | null;
	note: string | null;
	/** Stanowiska franczyzowe z heartbeatem < 7 dni; 0 = dymki nie mają gdzie dotrzeć. */
	activeDevices: number;
}

/**
 * `tenantId` = organizacja URZĄDZENIA z kafla, nie przełącznik organizacji:
 * panel pokazuje super_adminowi urządzenia wszystkich organizacji naraz, a
 * kanał powiadomień dopasowuje zgodę po `device_agents.tenant_id`.
 * Ten sam kształt ma zgoda na asystę informatyczną („Zgłoś problem” ze
 * stanowisk franczyzy) — `kind = "it-assist-optin"`.
 */
export type OptinKind = "notification-optin" | "it-assist-optin";

function optinPath(
	tenantId: number,
	idfirm?: number,
	kind: OptinKind = "notification-optin",
): string {
	const base = `/device-agent/${kind}`;
	return `${idfirm == null ? base : `${base}/${idfirm}`}?tenantId=${tenantId}`;
}

export function listNotificationOptin(
	tenantId: number,
	kind?: OptinKind,
): Promise<NotificationOptinEntry[]> {
	return apiFetch<NotificationOptinEntry[]>(optinPath(tenantId, undefined, kind));
}

export function enableNotificationOptin(
	tenantId: number,
	idfirm: number,
	note?: string,
	kind?: OptinKind,
): Promise<NotificationOptinEntry> {
	return apiFetch<NotificationOptinEntry>(optinPath(tenantId, idfirm, kind), {
		method: "PUT",
		body: JSON.stringify({ note: note ?? null }),
	});
}

export function disableNotificationOptin(
	tenantId: number,
	idfirm: number,
	kind?: OptinKind,
): Promise<void> {
	return apiFetch<void>(optinPath(tenantId, idfirm, kind), { method: "DELETE" });
}

/**
 * Wykluczenie APTEKI (`idfirm`) w organizacji URZĄDZENIA (`tenantId`) z grupy:
 * jej stanowiska tracą Asystenta, kanał powiadomień i „Otwórz publiczny KSeF”
 * w tray, a zdalny dostęp, synchronizacja i aktualizacje działają dalej.
 */
export interface GroupExclusionEntry {
	idfirm: number;
	excludedAt: string;
	/** Nazwa konta, które wykluczyło; null gdy konto usunięto albo wykluczał
	 *  super_admin z innej organizacji (aktor jest w audycie jego organizacji). */
	excludedBy: string | null;
	note: string | null;
}

function groupExclusionPath(tenantId: number, idfirm?: number): string {
	const base = "/device-agent/group-exclusion";
	return `${idfirm == null ? base : `${base}/${idfirm}`}?tenantId=${tenantId}`;
}

export function listGroupExclusions(tenantId: number): Promise<GroupExclusionEntry[]> {
	return apiFetch<GroupExclusionEntry[]>(groupExclusionPath(tenantId));
}

export function enableGroupExclusion(
	tenantId: number,
	idfirm: number,
	note?: string,
): Promise<GroupExclusionEntry> {
	return apiFetch<GroupExclusionEntry>(groupExclusionPath(tenantId, idfirm), {
		method: "PUT",
		body: JSON.stringify({ note: note ?? null }),
	});
}

export function disableGroupExclusion(tenantId: number, idfirm: number): Promise<void> {
	return apiFetch<void>(groupExclusionPath(tenantId, idfirm), { method: "DELETE" });
}

/** Saves (or clears) the per-device operator note. */
export function updateDeviceOperatorNote(
	deviceUuid: string,
	operatorNote: string,
): Promise<DeviceAgent> {
	return apiFetch<DeviceAgent>(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/operator-note`,
		{
			method: "PATCH",
			body: JSON.stringify({ operatorNote }),
		},
	);
}

/**
 * Roles assignable when approving a pending device. The backend derives the
 * `connectionPolicy` automatically from the role — it is NOT set here.
 * - `pharmacy_workstation` / `franchise` / `franchise_managed` — wymaga apteki (idfirm) + organizacji.
 * - `server` / `office_laptop` / `natubay` / `it_admin` — tylko organizacja.
 */
export type AssignableDeviceRole =
	| "pharmacy_workstation"
	| "office_laptop"
	| "it_admin"
	| "server"
	| "natubay"
	| "franchise"
	| "franchise_managed"
	| "franchise_other";

export interface UpdateDeviceAssignmentInput {
	tenantId: number;
	idfirm?: number | null;
	deviceRole: AssignableDeviceRole;
	displayName?: string | null;
}

/**
 * Poprawia przypisanie już zatwierdzonego urządzenia (rola / organizacja /
 * apteka / nazwa) — gdy ktoś pomylił się przy zatwierdzaniu. Backend czyści
 * idfirm automatycznie, gdy rola nie jest apteczna.
 */
export function updateDeviceAssignment(
	deviceUuid: string,
	body: UpdateDeviceAssignmentInput,
): Promise<DeviceAgent> {
	return apiFetch<DeviceAgent>(`/device-agent/devices/${encodeURIComponent(deviceUuid)}`, {
		method: "PATCH",
		body: JSON.stringify({
			tenantId: body.tenantId,
			idfirm: body.idfirm ?? null,
			deviceRole: body.deviceRole,
			displayName: body.displayName ?? null,
		}),
	});
}

export interface ApproveDeviceInput {
	tenantId: number;
	idfirm?: number | null;
	deviceRole: AssignableDeviceRole;
	displayName?: string;
}

/**
 * Approves a pending (`enrolled_unassigned`) device — assigns it a role + scope
 * (tenant, optionally pharmacy idfirm) and moves it out of the approval queue.
 */
export function approveDevice(deviceUuid: string, body: ApproveDeviceInput): Promise<DeviceAgent> {
	return apiFetch<DeviceAgent>(`/device-agent/devices/${encodeURIComponent(deviceUuid)}/approve`, {
		method: "POST",
		body: JSON.stringify({
			tenantId: body.tenantId,
			idfirm: body.idfirm ?? null,
			deviceRole: body.deviceRole,
			displayName: body.displayName,
		}),
	});
}

/** Confirms a fingerprint-matched reinstall and restores the previous assignment. */
export function recoverDevice(candidateUuid: string): Promise<DeviceAgent> {
	return apiFetch<DeviceAgent>(
		`/device-agent/devices/${encodeURIComponent(candidateUuid)}/recover`,
		{ method: "POST" },
	);
}

/**
 * Rejects a pending device (or revokes an assigned one) — the backend marks it
 * revoked and removes it from the approval queue.
 */
export function revokeDevice(deviceUuid: string): Promise<DeviceAgent> {
	return apiFetch<DeviceAgent>(`/device-agent/devices/${encodeURIComponent(deviceUuid)}/revoke`, {
		method: "POST",
	});
}

/**
 * Hard-delete a device — trwale usuwa wpis z książki adresowej (w odróżnieniu od
 * `revokeDevice`, które zostawia wpis oznaczony jako odwołany). Backend zwraca
 * 204 No Content, więc nie ma ciała odpowiedzi.
 *
 * `uninstall=true` zleca agentowi samodeinstalację z hosta (jeśli online) PRZED
 * usunięciem wpisu; offline → flaga jest ignorowana, kasujemy tylko wpis.
 */
export function deleteDevice(deviceUuid: string, uninstall = false): Promise<void> {
	return apiFetch<void>(`/device-agent/devices/${encodeURIComponent(deviceUuid)}/delete`, {
		method: "POST",
		body: JSON.stringify({ uninstall }),
	});
}

// ── Katalog operacji device-agenta (podpisane komendy `run_operation`) ───────
//
// Operacje to DANE podpisane kluczem serwera, nie kod w binarce — dochodzą bez
// update floty. Backend: `require_super_admin`. Ta iteracja obsługuje wyłącznie
// operacje BEZ parametrów (`params: []`); gdy pojawi się operacja z parametrami,
// trzeba dołożyć UI wypełniające `{ params: {...} }` w body.

export interface DeviceOperation {
	operationId: string;
	label: string;
	/** true → zdejmuje zabezpieczenie systemu; UI wymusza dialog potwierdzenia. */
	destructive: boolean;
	/** Definicja parametrów. Pusta lista = operacja bez parametrów (v1). */
	params: unknown[];
}

export interface RunOperationResponse {
	commandId: number;
	operationId: string;
	/** true = komenda trafiła do kolejki urządzenia; NIE oznacza wykonania. */
	queued: boolean;
}

/** Podpisany katalog operacji do uruchomienia z sekcji diagnostyki (super_admin). */
export function listDeviceOperations(): Promise<DeviceOperation[]> {
	return apiFetch<DeviceOperation[]>("/device-agent/operations");
}

/**
 * Zleca operację z katalogu na urządzeniu — backend renderuje kroki, podpisuje
 * je i wrzuca do kolejki komend. Body `{}` (operacja bez parametrów).
 */
export function runDeviceOperation(
	deviceUuid: string,
	operationId: string,
): Promise<RunOperationResponse> {
	return apiFetch<RunOperationResponse>(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/operations/${encodeURIComponent(
			operationId,
		)}/run`,
		{ method: "POST", body: JSON.stringify({}) },
	);
}

export function createRemoteSession(input: CreateRemoteSessionInput): Promise<RemoteSession> {
	return apiFetch<RemoteSession>("/device-agent/remote-sessions", {
		method: "POST",
		body: JSON.stringify({
			targetDeviceUuid: input.targetDeviceUuid,
			controllerDeviceUuid: input.controllerDeviceUuid,
		}),
	});
}

export function getRemoteSession(sessionId: string): Promise<RemoteSession> {
	return apiFetch<RemoteSession>(`/device-agent/remote-sessions/${encodeURIComponent(sessionId)}`);
}

// ─── Installer & enrollment tokens (Phase 5 — operator self-service) ─────────

/**
 * Metadata for the published Windows installer. `available=false` means the
 * .exe has not been uploaded yet — the download button stays disabled.
 */
export interface InstallerMetadata {
	available: boolean;
	/** Staly publiczny URL pobrania instalatora BEZ logowania (do przekazania komukolwiek). */
	publicUrl: string;
	/** Wersja wydania instalatora (sidecar .version z device-agent/VERSION). */
	version?: string;
	fileName?: string;
	sizeBytes?: number;
	sha256?: string;
	updatedAt?: string;
}

/**
 * Result of minting a one-time enrollment token. `token` is the RAW secret —
 * returned ONCE on creation and never again; the operator pastes it (plus
 * `backendUrl`) into the installer.
 */
export interface EnrollmentTokenResult {
	token: string;
	tokenId: number;
	expiresAt: string;
	maxSeats: number;
	label?: string;
	backendUrl: string;
}

/** A row from the active-tokens list — never carries the raw secret. */
export interface EnrollmentTokenSummary {
	tokenId: number;
	label?: string;
	tenantId?: number | null;
	idfirm?: number | null;
	maxSeats: number;
	usedSeats: number;
	expiresAt: string;
	createdAt: string;
	createdBy?: string;
}

export interface CreateEnrollmentTokenInput {
	tenantId?: number | null;
	idfirm?: number | null;
	label?: string;
	ttlHours?: number;
	maxSeats?: number;
}

export function getInstallerMetadata(): Promise<InstallerMetadata> {
	return apiFetch<InstallerMetadata>("/device-agent/installer/metadata");
}

/** Metadata of the FarmasterAgent Lite installer (non-enrolled computers). */
export function getLiteInstallerMetadata(): Promise<InstallerMetadata> {
	return apiFetch<InstallerMetadata>("/device-agent/installer/lite/metadata");
}

/**
 * Connection secret for AGENTLESS controllers (e.g. a Mac dialing the RustDesk
 * ID + password by hand). SUPER_ADMIN only on the backend. Fetched on demand
 * (reveal click) — never auto-loaded, never part of a list response.
 */
export interface ConnectionSecret {
	rustdeskId: string | null;
	unattendedPassword: string | null;
}

export function getConnectionSecret(deviceUuid: string): Promise<ConnectionSecret> {
	return apiFetch<ConnectionSecret>(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/connection-secret`,
	);
}

/**
 * Streams the Windows installer .exe and triggers a browser download. Uses
 * `apiFetchBlob` so the Bearer token + tenant override are attached, then the
 * established anchor-click + object-URL pattern (see `lib/export-excel.ts`).
 */
export async function downloadInstaller(): Promise<void> {
	const { blob, response } = await apiFetchBlob("/device-agent/installer/windows");

	// Prefer the server-provided filename from Content-Disposition; fall back to
	// a sensible default so the download always has an .exe name.
	const disposition = response.headers.get("Content-Disposition") ?? "";
	const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
	const fileName = match ? decodeURIComponent(match[1]) : "farmaster-agent-setup.exe";

	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = fileName;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}

/** Streams the FarmasterAgent Lite installer .exe and triggers a download. */
export async function downloadLiteInstaller(): Promise<void> {
	const { blob, response } = await apiFetchBlob("/device-agent/installer/lite/windows");

	const disposition = response.headers.get("Content-Disposition") ?? "";
	const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
	const fileName = match ? decodeURIComponent(match[1]) : "farmaster-agent-lite-setup.exe";

	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = fileName;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}

// ─── Log bundles (Phase 6 — on-demand log collection) ───────────────────────

/**
 * Terminal/in-flight states of an on-demand log bundle. `collecting` means the
 * agent is still streaming `diagnostics_chunk` frames; `ready` means the tar.gz
 * is assembled on the backend volume and can be downloaded; `failed` means the
 * agent never delivered a complete bundle (timeout, sha mismatch, agent error).
 */
export type LogBundleStatus = "collecting" | "ready" | "failed";

export interface LogBundle {
	bundleId: string;
	status: LogBundleStatus;
	sizeBytes?: number;
	createdAt: string;
	completedAt?: string | null;
	failureReason?: string | null;
}

/**
 * Enqueues a `request_log_bundle` command for the device and returns the freshly
 * minted bundle (the backend generates the `bundleId`). Dedup is server-side:
 * one `collecting` bundle per device at a time.
 */
export function requestLogBundle(deviceUuid: string): Promise<LogBundle> {
	return apiFetch<LogBundle>(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/log-bundles`,
		{ method: "POST" },
	);
}

/** Polls the status of a single log bundle until it reaches `ready`/`failed`. */
export function getLogBundleStatus(deviceUuid: string, bundleId: string): Promise<LogBundle> {
	return apiFetch<LogBundle>(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/log-bundles/${encodeURIComponent(
			bundleId,
		)}`,
	);
}

/**
 * Streams the assembled tar.gz and triggers a browser download. Same blob +
 * anchor-click pattern as `downloadInstaller`; the filename comes from the
 * server `Content-Disposition` with a sensible fallback.
 */
export async function downloadLogBundle(deviceUuid: string, bundleId: string): Promise<void> {
	const { blob, response } = await apiFetchBlob(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/log-bundles/${encodeURIComponent(
			bundleId,
		)}/download`,
	);

	const disposition = response.headers.get("Content-Disposition") ?? "";
	const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
	const fileName = match ? decodeURIComponent(match[1]) : `farmaster-logs-${deviceUuid}.tar.gz`;

	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = fileName;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}

export function createEnrollmentToken(
	input: CreateEnrollmentTokenInput,
): Promise<EnrollmentTokenResult> {
	return apiFetch<EnrollmentTokenResult>("/device-agent/enrollment-tokens", {
		method: "POST",
		body: JSON.stringify({
			tenantId: input.tenantId ?? null,
			idfirm: input.idfirm ?? null,
			label: input.label,
			ttlHours: input.ttlHours,
			maxSeats: input.maxSeats,
		}),
	});
}

export function listEnrollmentTokens(): Promise<EnrollmentTokenSummary[]> {
	return apiFetch<EnrollmentTokenSummary[]>("/device-agent/enrollment-tokens");
}

export function revokeEnrollmentToken(tokenId: number): Promise<void> {
	return apiFetch<void>(`/device-agent/enrollment-tokens/${tokenId}/revoke`, {
		method: "POST",
	});
}

// ── Hasła serwisowe agenta (public.settings, szyfrowane at-rest) ─────────────
//
// Klucze 1:1 z backendem (`services/device_agent.rs`): backend czyta pole
// `password` i dosyła je agentowi w ramce komendy `unlock_service_account` /
// `db_online`. Zła nazwa klucza = przycisk oddaje „nie jest ustawione”.

export const SERVICE_SECRET_KEYS = {
	serwis: "device_agent_serwis",
	sysdba: "device_agent_sysdba",
} as const;

export type ServiceSecretKey = (typeof SERVICE_SECRET_KEYS)[keyof typeof SERVICE_SECRET_KEYS];

interface SettingResponse {
	key: string;
	value: { password?: string } | null;
}

/** Zamaskowana wartość (`ab••••cd`) albo `null`, gdy nikt jeszcze nie wpisał hasła. */
export async function fetchServiceSecretMasked(key: ServiceSecretKey): Promise<string | null> {
	const res = await apiFetch<SettingResponse>(`/admin/settings/${key}`);
	const masked = res.value?.password ?? "";
	return masked === "" ? null : masked;
}

export function saveServiceSecret(key: ServiceSecretKey, password: string): Promise<unknown> {
	return apiFetch(`/admin/settings/${key}`, {
		method: "PUT",
		body: JSON.stringify({ value: { password }, encrypted: true }),
	});
}
